/**
 * SSE(text/event-stream) 응답 본문에서 `data: {...}` 줄을 JSON 으로 하나씩 꺼낸다.
 *
 *   for await (const event of readSseJson(response.body, { idleTimeoutMs, idleTimeoutError })) { ... }
 *
 * - 한글 바이트가 조각 경계에서 잘려도 깨지지 않게 TextDecoder 를 stream 모드로 쓴다.
 * - JSON 이 아닌 data 줄(`[DONE]` 등), 주석(`: ping`), event 줄은 건너뛴다.
 * - 끝까지 읽거나 도중에 멈추면 lock 만 푼다. 서버가 complete 뒤에 저장을 마치는 스트림이 있어서
 *   기본으로 연결을 끊지 않는다. 끊어야 하는 곳만 cancelOnExit 을 켠다.
 *
 * @param {ReadableStream} body
 * @param {Object} [options]
 * @param {number} [options.idleTimeoutMs] 이 시간 동안 조각이 없으면 연결을 닫는다(0 이면 끔).
 * @param {(() => Error)|null} [options.idleTimeoutError] 시간 초과 때 던질 오류. null 이면 오류 없이 끝낸다.
 * @param {(error: Error) => Error} [options.mapReadError] 연결 오류(read 실패)를 바꿀 때. 읽는 쪽 코드의 오류에는 쓰지 않는다.
 * @param {() => void} [options.beforeRead] 매 read 전에 부른다. 던지면 연결을 닫고 그 오류를 올린다.
 * @param {(data: string) => void} [options.onInvalidData] JSON 으로 못 읽은 data 를 알려준다(`[DONE]` 제외).
 * @param {boolean} [options.cancelOnExit] 끝나거나 멈출 때 연결을 닫는다.
 */
export async function* readSseJson(body, {
  idleTimeoutMs = 0,
  idleTimeoutError = () => new Error('SSE_IDLE_TIMEOUT'),
  mapReadError = (error) => error,
  beforeRead,
  onInvalidData,
  cancelOnExit = false,
} = {}) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const cancel = () => {
    try {
      Promise.resolve(reader.cancel?.()).catch(() => {});
    } catch (_) {
      // 이미 닫힌 스트림
    }
  };
  let buffer = '';
  let cancelled = false;

  const parse = (line) => {
    const clean = line.endsWith('\r') ? line.slice(0, -1) : line;
    if (!clean.startsWith('data: ')) return undefined;
    const data = clean.slice(6);
    if (!data || data === '[DONE]') return undefined;
    try {
      return JSON.parse(data);
    } catch (_) {
      onInvalidData?.(data);
      return undefined;
    }
  };

  const read = async () => {
    if (!idleTimeoutMs) return { result: await reader.read() };
    let timer;
    const idle = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ idle: true }), idleTimeoutMs);
    });
    try {
      return await Promise.race([reader.read().then((result) => ({ result })), idle]);
    } finally {
      clearTimeout(timer);
    }
  };

  try {
    while (true) {
      try {
        beforeRead?.();
      } catch (error) {
        cancelled = true;
        cancel();
        throw error;
      }
      let next;
      try {
        next = await read();
      } catch (error) {
        cancelled = true;
        cancel();
        throw mapReadError(error);
      }
      if (next.idle) {
        cancelled = true;
        cancel();
        if (idleTimeoutError) throw idleTimeoutError();
        return;
      }
      const { done, value } = next.result;
      if (done) {
        buffer += decoder.decode();
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const event = parse(line);
        if (event !== undefined) yield event;
      }
    }
    for (const line of buffer.split('\n')) {
      const event = parse(line);
      if (event !== undefined) yield event;
    }
  } finally {
    if (cancelOnExit && !cancelled) cancel();
    try {
      reader.releaseLock?.();
    } catch (_) {
      // 읽는 중이던 요청이 남아 있으면 lock 을 못 푼다
    }
  }
}
